import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { HeroButton } from '@/components/ui/button-variants';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import EditPetModal from '@/components/Profile/EditPetModal';
import CreatePostModal from '@/components/Profile/CreatePostModal';
import PetPassportModal from '@/components/Profile/PetPassportModal';
import ProfileGrid from '@/components/Profile/ProfileGrid';
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  Heart, 
  MessageCircle,
  Share2,
  Camera,
  Grid3X3,
  Settings,
  Plus,
  Cake,
  Info,
  FileText
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface Pet {
  id: string;
  user_id: string;
  name: string;
  species: string;
  breed: string | null;
  age: number | null;
  gender: string | null;
  description: string | null;
  avatar_url: string | null;
  is_public: boolean;
  created_at: string;
  posts_count: number;
}

interface Post {
  id: string;
  content: string | null;
  image_url: string | null;
  likes_count: number;
  comments_count: number;
  created_at: string;
  pet_id: string | null;
  location: string | null;
  pets: {
    name: string;
    avatar_url: string | null;
  } | null;
}

const PetProfile = () => {
  const { petId } = useParams<{ petId: string }>();
  const { user, loading: authLoading } = useAuth();
  const [pet, setPet] = useState<Pet | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createPostOpen, setCreatePostOpen] = useState(false);
  const [passportOpen, setPassportOpen] = useState(false);

  useEffect(() => {
    if (petId) {
      fetchPetProfile();
      fetchPetPosts();
    }
  }, [petId, user]);

  const fetchPetProfile = async () => {
    if (!petId) return;

    try {
      const { data, error } = await supabase
        .from('pets')
        .select('*')
        .eq('id', petId)
        .single();

      if (error) throw error;

      if (!data) {
        toast({
          title: "Pet non trovato",
          description: "Il pet che stai cercando non esiste.",
          variant: "destructive",
        });
        return;
      }

      // Check if it's a private pet and user doesn't own it
      if (!data.is_public && (!user || data.user_id !== user.id)) {
        toast({
          title: "Profilo privato",
          description: "Questo profilo pet è privato.",
          variant: "destructive",
        });
        return;
      }

      // Get posts count
      const { count } = await supabase
        .from('posts')
        .select('id', { count: 'exact', head: true })
        .eq('pet_id', petId);

      setPet({
        ...data,
        posts_count: count || 0,
      });

      setIsOwner(user?.id === data.user_id);
    } catch (error) {
      console.error('Error fetching pet profile:', error);
      toast({
        title: "Errore",
        description: "Non è stato possibile caricare il profilo del pet.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPetPosts = async () => {
    if (!petId) return;

    try {
      const { data, error } = await supabase
        .from('posts')
        .select(`
          *,
          pets (name, avatar_url)
        `)
        .eq('pet_id', petId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching pet posts:', error);
    }
  };

  const getSpeciesEmoji = (species: string) => {
    switch (species.toLowerCase()) {
      case 'cane': return '🐕';
      case 'gatto': return '🐱';
      case 'coniglio': return '🐰';
      case 'uccello': return '🐦';
      case 'pesce': return '🐠';
      case 'tartaruga': return '🐢';
      case 'criceto': return '🐹';
      case 'furetto': return '🦫';
      default: return '🐾';
    }
  };

  const getGenderIcon = (gender: string | null) => {
    if (!gender) return '';
    return gender === 'maschio' ? '♂️' : '♀️';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Redirect to auth if not authenticated and trying to access private pet
  if (!authLoading && !user && pet && !pet.is_public) {
    return <Navigate to="/auth" replace />;
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-8 h-8 bg-muted rounded animate-pulse" />
          <div className="h-6 bg-muted rounded w-32 animate-pulse" />
        </div>
        
        <Card className="glass border-white/20">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="w-32 h-32 bg-muted rounded-full animate-pulse" />
              <div className="flex-1 space-y-4">
                <div className="space-y-2">
                  <div className="h-8 bg-muted rounded w-48 animate-pulse" />
                  <div className="h-4 bg-muted rounded w-32 animate-pulse" />
                </div>
                <div className="flex gap-6">
                  <div className="text-center space-y-1">
                    <div className="h-6 w-12 bg-muted rounded animate-pulse" />
                    <div className="h-4 w-16 bg-muted rounded animate-pulse" />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!pet) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="text-center py-16">
          <Camera className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Pet non trovato</h3>
          <p className="text-muted-foreground mb-6">
            Il pet che stai cercando non esiste o non hai i permessi per visualizzarlo.
          </p>
          <Link to="/app/profile">
            <HeroButton>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna al Profilo
            </HeroButton>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 pb-20 lg:pb-4">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <Link 
          to="/app/profile" 
          className="p-2 glass rounded-full hover:bg-white/10 transition-smooth"
        >
          <ArrowLeft className="w-5 h-5 text-white" />
        </Link>
        <h1 className="text-xl font-semibold text-white">{pet.name}</h1>
        {isOwner && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditModalOpen(true)}
            className="ml-auto text-white hover:bg-white/10"
          >
            <Settings className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Pet Profile Header */}
      <Card className="glass border-white/20 mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <Avatar className="w-32 h-32 ring-4 ring-primary/30">
                <AvatarImage src={pet.avatar_url || undefined} />
                <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white text-4xl">
                  {getSpeciesEmoji(pet.species)}
                </AvatarFallback>
              </Avatar>
            </div>

            {/* Info */}
            <div className="flex-1 space-y-4">
              {/* Name and Species */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-2xl font-bold text-white">{pet.name}</h2>
                  <Badge variant="secondary" className="text-sm">
                    {getSpeciesEmoji(pet.species)} {pet.species}
                  </Badge>
                  {pet.gender && (
                    <Badge variant="outline" className="text-sm">
                      {getGenderIcon(pet.gender)} {pet.gender}
                    </Badge>
                  )}
                  {!pet.is_public && (
                    <Badge variant="secondary" className="text-sm">
                      🔒 Privato
                    </Badge>
                  )}
                </div>
                
                {pet.breed && (
                  <p className="text-muted-foreground">{pet.breed}</p>
                )}
              </div>

              {/* Stats */}
              <div className="flex gap-6">
                <div className="text-center">
                  <div className="text-xl font-bold text-white">{pet.posts_count}</div>
                  <div className="text-sm text-muted-foreground">Post</div>
                </div>
                {pet.age && (
                  <div className="text-center">
                    <div className="text-xl font-bold text-white">{pet.age}</div>
                    <div className="text-sm text-muted-foreground">
                      {pet.age === 1 ? 'Anno' : 'Anni'}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons for Owner - Moved inside card for mobile */}
              {isOwner && (
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4">
                  <HeroButton
                    onClick={() => setCreatePostOpen(true)}
                    className="flex-1 text-sm sm:text-base"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Nuovo Post per {pet.name}</span>
                    <span className="sm:hidden">Nuovo Post</span>
                  </HeroButton>
                  <Button
                    onClick={() => setPassportOpen(true)}
                    variant="outline"
                    className="border-primary/30 text-primary hover:bg-primary/10 text-sm sm:text-base"
                    size="sm"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Passaporto
                  </Button>
                </div>
              )}

              {/* Description */}
              {pet.description && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-white">Descrizione</span>
                  </div>
                  <p className="text-white">{pet.description}</p>
                </div>
              )}

              {/* Meta Info */}
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>Membro dal {formatDate(pet.created_at)}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content Tabs */}
      <Tabs defaultValue="posts" className="space-y-6">
        <TabsList className="w-full glass border-white/20">
          <TabsTrigger value="posts" className="flex-1 data-[state=active]:bg-primary">
            <Grid3X3 className="w-4 h-4 mr-2" />
            Post ({posts.length})
          </TabsTrigger>
        </TabsList>

        {/* Posts Tab */}
        <TabsContent value="posts" className="space-y-6">
          {posts.length === 0 ? (
            <Card className="glass border-white/20">
              <CardContent className="p-8 text-center">
                <Camera className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  {isOwner ? `Nessun post di ${pet.name}` : `${pet.name} non ha ancora condiviso nulla`}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {isOwner 
                    ? `Condividi i momenti speciali di ${pet.name}!`
                    : `Quando ${pet.name} condividerà qualcosa, apparirà qui.`
                  }
                </p>
                {isOwner && (
                  <HeroButton onClick={() => setCreatePostOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Primo Post di {pet.name}
                  </HeroButton>
                )}
              </CardContent>
            </Card>
          ) : (
            <ProfileGrid posts={posts} />
          )}
        </TabsContent>
      </Tabs>

      {/* Modals */}
      {isOwner && (
        <>
          <EditPetModal
            open={editModalOpen}
            onOpenChange={setEditModalOpen}
            pet={pet}
            onSuccess={() => {
              fetchPetProfile();
              toast({
                title: "Profilo aggiornato",
                description: `${pet.name} è stato aggiornato con successo.`,
              });
            }}
          />
          
          <CreatePostModal
            open={createPostOpen}
            onOpenChange={setCreatePostOpen}
            selectedPetId={pet.id}
            onSuccess={() => {
              fetchPetPosts();
              fetchPetProfile(); // Refresh to update post count
            }}
          />
          
          <PetPassportModal
            open={passportOpen}
            onOpenChange={setPassportOpen}
            pet={pet}
            onSuccess={() => {
              // Optionally refresh any data that might be affected
            }}
          />
        </>
      )}
    </div>
  );
};

export default PetProfile;